/**
 * Solo playtest setup screen.
 *
 * Two-player local hotseat: pick names + decks for both sides, then start.
 * On start, we call setupGame() and navigate to /playtest/board with the
 * fresh GameState in route state. Visual layout matches the reference
 * screenshot 08 — Player 1 panel, Player 2 panel, Start Match button.
 *
 * If no legal decks are available, we show a hint banner pointing to /decks.
 */
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { useStore } from "@/hooks/useStore";
import type { Card } from "@/types/card";
import type { Deck } from "@/types/deck";
import { setupGame } from "@/game/engine";
import { Swords, AlertCircle } from "lucide-react";
import { toast } from "sonner";

function buildCardMap(cards: Card[]): Map<string, Card> {
  const m = new Map<string, Card>();
  for (const c of cards) m.set(c.id, c);
  return m;
}

export function PlaytestSolo() {
  const { state, ready } = useStore();
  const navigate = useNavigate();

  const [name1, setName1] = useState("Player 1");
  const [name2, setName2] = useState("Player 2");
  const [deck1Id, setDeck1Id] = useState<string>("");
  const [deck2Id, setDeck2Id] = useState<string>("");

  const cardMap = useMemo(() => buildCardMap(state.cards), [state.cards]);
  const decks = state.decks;
  const legalDecks = decks.filter((d) => d.legality.status === "legal");
  const enoughDecks = legalDecks.length >= 1; // any legal deck is fine; both can use the same.

  function start() {
    const d1: Deck | undefined =
      decks.find((d) => d.id === deck1Id) ?? legalDecks[0] ?? decks[0];
    const d2: Deck | undefined =
      decks.find((d) => d.id === deck2Id) ?? legalDecks[0] ?? decks[0];
    if (!d1 || !d2) {
      toast.error("Build at least one deck first");
      return;
    }
    const gs = setupGame(name1.trim() || "Player 1", name2.trim() || "Player 2", d1, d2, cardMap);
    navigate("/playtest/board", { state: { game: gs } });
  }

  if (!ready) {
    return <p className="p-6 text-sm text-[#64748b]">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <div>
        <PageTitle>Playtest Setup</PageTitle>
        <p className="mt-1 text-sm text-[#64748b]">
          Hotseat match — 2 players, 1 screen
        </p>
      </div>

      {!enoughDecks && (
        <div className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>
            You need at least 1 legal deck to start a match. Build and finalize decks in the{" "}
            <Link to="/decks" className="underline hover:text-red-300">
              Decks
            </Link>{" "}
            section.
          </span>
        </div>
      )}

      <div className="max-w-2xl space-y-4">
        <Panel className="space-y-3">
          <h2 className="font-display text-base font-semibold text-[#e2e8f0]">Player 1</h2>
          <label className="block">
            <span className="gm-label">Name</span>
            <input
              className="gm-input mt-1 w-full"
              value={name1}
              onChange={(e) => setName1(e.target.value)}
              placeholder="Player 1"
            />
          </label>
          <label className="block">
            <span className="gm-label">Deck</span>
            <select
              className="gm-input mt-1 w-full"
              value={deck1Id}
              onChange={(e) => setDeck1Id(e.target.value)}
            >
              <option value="">Select a deck</option>
              {decks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.deckName}
                  {d.legality.status !== "legal" ? ` — ${d.legality.status}` : ""}
                </option>
              ))}
            </select>
          </label>
        </Panel>

      </div>

      <div className="max-w-2xl">
        <Panel className="space-y-3">
          <h2 className="font-display text-base font-semibold text-[#e2e8f0]">Player 2</h2>
          <label className="block">
            <span className="gm-label">Name</span>
            <input
              className="gm-input mt-1 w-full"
              value={name2}
              onChange={(e) => setName2(e.target.value)}
              placeholder="Player 2"
            />
          </label>
          <label className="block">
            <span className="gm-label">Deck</span>
            <select
              className="gm-input mt-1 w-full"
              value={deck2Id}
              onChange={(e) => setDeck2Id(e.target.value)}
            >
              <option value="">Select a deck</option>
              {decks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.deckName}
                  {d.legality.status !== "legal" ? ` — ${d.legality.status}` : ""}
                </option>
              ))}
            </select>
          </label>
        </Panel>
      </div>

      <div className="max-w-2xl">
        <Button onClick={start} disabled={!enoughDecks} className="w-full gap-2">
          <Swords size={14} /> Start Match
        </Button>
      </div>
    </div>
  );
}
